import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Plus, Search, User, FileText, CheckCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Encounters = () => {
  const [encounters, setEncounters] = useState([]);
  const [patients, setPatients] = useState([]);
  const [billingStatuses, setBillingStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    date: new Date().toISOString().split('T')[0],
    // Narrative sections
    presenting_complaint: '',
    history_of_presenting_complaint: '',
    examination_findings: '',
    assessment: '',
    plan: '',
    // Vitals
    temperature: '',
    bp_systolic: '',
    bp_diastolic: '',
    pulse_rate: '',
    oxygen_saturation: '',
    gmr: '',
    dip_stick: '',
    height: '',
    weight: ''
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
    const patientId = searchParams.get('patient_id');
    if (searchParams.get('action') === 'new') {
      setShowModal(true);
      if (patientId) {
        setFormData(prev => ({ ...prev, patient_id: patientId }));
      }
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [encountersRes, patientsRes] = await Promise.all([
        axios.get(`${API}/encounters`),
        axios.get(`${API}/patients`)
      ]);
      setEncounters(encountersRes.data);
      setPatients(patientsRes.data);
      
      // Fetch billing statuses for all encounters
      const statusMap = {};
      await Promise.all(
        encountersRes.data.map(async (encounter) => {
          try {
            const statusRes = await axios.get(`${API}/encounters/${encounter.id}/billing-status`);
            statusMap[encounter.id] = statusRes.data;
          } catch (err) {
            // Ignore errors for individual billing status fetches
          }
        })
      );
      setBillingStatuses(statusMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load encounters');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      date: new Date().toISOString().split('T')[0],
      presenting_complaint: '',
      history_of_presenting_complaint: '',
      examination_findings: '',
      assessment: '',
      plan: '',
      temperature: '',
      bp_systolic: '',
      bp_diastolic: '',
      pulse_rate: '',
      oxygen_saturation: '',
      gmr: '',
      dip_stick: '',
      height: '',
      weight: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_id) {
      toast.error('Please select a patient');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/encounters`, formData);
      toast.success('Encounter created successfully');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create encounter');
    } finally {
      setSaving(false);
    }
  };

  const filteredEncounters = encounters.filter(e =>
    e.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.presenting_complaint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.assessment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="page-container" data-testid="encounters-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Encounters</h1>
            <p className="text-slate-500 mt-1">{encounters.length} total encounters</p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-encounter-btn"
          >
            <Plus className="w-4 h-4" />
            New Encounter
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search encounters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Encounters List */}
        <div className="card" data-testid="encounters-list">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredEncounters.length > 0 ? (
            <div>
              {filteredEncounters.map((encounter) => (
                <div
                  key={encounter.id}
                  className="patient-row"
                  onClick={() => navigate(`/encounters/${encounter.id}`)}
                  data-testid={`encounter-item-${encounter.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      encounter.signed_at ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {encounter.signed_at ? <CheckCircle className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">
                          {encounter.presenting_complaint?.substring(0, 50) || 'No presenting complaint'}
                          {encounter.presenting_complaint?.length > 50 ? '...' : ''}
                        </p>
                        {encounter.signed_at && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Signed</span>
                        )}
                        {/* OPEN BILL Indicator */}
                        {billingStatuses[encounter.id]?.has_open_bill && (
                          <span 
                            className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1"
                            data-testid={`open-bill-indicator-${encounter.id}`}
                          >
                            <DollarSign className="w-3 h-3" />
                            OPEN BILL
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="text-sm text-slate-500">{encounter.patient_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Calendar className="w-4 h-4" />
                      <span>{encounter.date}</span>
                    </div>
                    {encounter.assessment && (
                      <p className="text-sm text-slate-400 mt-1 truncate max-w-[200px]">
                        {encounter.assessment}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Calendar className="empty-state-icon" />
              <p className="empty-state-title">No encounters found</p>
              <p className="empty-state-text">
                {searchTerm ? 'Try a different search term' : 'Create your first encounter'}
              </p>
            </div>
          )}
        </div>

        {/* Add Encounter Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-encounter-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">New Encounter</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Patient & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="patient_id">Patient *</Label>
                  <Select
                    value={formData.patient_id}
                    onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
                  >
                    <SelectTrigger data-testid="encounter-patient-select">
                      <SelectValue placeholder="Select a patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id}>
                          {patient.full_name} {patient.middle_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="encounter-date-input"
                  />
                </div>
              </div>

              {/* Vitals & Examination */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Vitals & Examination</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label htmlFor="temperature">Temp (°C)</Label>
                    <Input
                      id="temperature"
                      value={formData.temperature}
                      onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                      placeholder="37.0"
                      data-testid="encounter-temp-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bp">BP (mmHg)</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.bp_systolic}
                        onChange={(e) => setFormData({ ...formData, bp_systolic: e.target.value })}
                        placeholder="120"
                        className="w-1/2"
                        data-testid="encounter-bp-sys-input"
                      />
                      <span className="flex items-center text-slate-400">/</span>
                      <Input
                        value={formData.bp_diastolic}
                        onChange={(e) => setFormData({ ...formData, bp_diastolic: e.target.value })}
                        placeholder="80"
                        className="w-1/2"
                        data-testid="encounter-bp-dia-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="pulse_rate">Pulse (bpm)</Label>
                    <Input
                      id="pulse_rate"
                      value={formData.pulse_rate}
                      onChange={(e) => setFormData({ ...formData, pulse_rate: e.target.value })}
                      placeholder="72"
                      data-testid="encounter-pulse-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="oxygen_saturation">SpO₂ (%)</Label>
                    <Input
                      id="oxygen_saturation"
                      value={formData.oxygen_saturation}
                      onChange={(e) => setFormData({ ...formData, oxygen_saturation: e.target.value })}
                      placeholder="98"
                      data-testid="encounter-spo2-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gmr">GMR (mg/dL)</Label>
                    <Input
                      id="gmr"
                      value={formData.gmr}
                      onChange={(e) => setFormData({ ...formData, gmr: e.target.value })}
                      placeholder="100"
                      data-testid="encounter-gmr-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dip_stick">Dip Stick</Label>
                    <Input
                      id="dip_stick"
                      value={formData.dip_stick}
                      onChange={(e) => setFormData({ ...formData, dip_stick: e.target.value })}
                      placeholder="Results"
                      data-testid="encounter-dipstick-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="height">Height (cm)</Label>
                    <Input
                      id="height"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      placeholder="170"
                      data-testid="encounter-height-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weight">Weight (kg)</Label>
                    <Input
                      id="weight"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      placeholder="70"
                      data-testid="encounter-weight-input"
                    />
                  </div>
                </div>
              </div>

              {/* Clinical Narrative */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Clinical Narrative</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="presenting_complaint" className="flex items-center gap-2">
                      Presenting Complaint
                    </Label>
                    <Textarea
                      id="presenting_complaint"
                      value={formData.presenting_complaint}
                      onChange={(e) => setFormData({ ...formData, presenting_complaint: e.target.value })}
                      placeholder="Patient's chief complaint..."
                      rows={2}
                      data-testid="encounter-pc-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="history_of_presenting_complaint" className="flex items-center gap-2">
                      History of Presenting Complaint
                    </Label>
                    <Textarea
                      id="history_of_presenting_complaint"
                      value={formData.history_of_presenting_complaint}
                      onChange={(e) => setFormData({ ...formData, history_of_presenting_complaint: e.target.value })}
                      placeholder="Onset, duration, severity, associated symptoms..."
                      rows={3}
                      data-testid="encounter-hpc-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="examination_findings" className="flex items-center gap-2">
                      Examination Findings
                    </Label>
                    <Textarea
                      id="examination_findings"
                      value={formData.examination_findings}
                      onChange={(e) => setFormData({ ...formData, examination_findings: e.target.value })}
                      placeholder="Physical examination findings..."
                      rows={3}
                      data-testid="encounter-exam-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="assessment" className="flex items-center gap-2">
                      Assessment
                    </Label>
                    <Textarea
                      id="assessment"
                      value={formData.assessment}
                      onChange={(e) => setFormData({ ...formData, assessment: e.target.value })}
                      placeholder="Diagnosis or clinical impression..."
                      rows={2}
                      data-testid="encounter-assessment-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="plan" className="flex items-center gap-2">
                      Plan
                    </Label>
                    <Textarea
                      id="plan"
                      value={formData.plan}
                      onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                      placeholder="Treatment plan, prescriptions, follow-up..."
                      rows={3}
                      data-testid="encounter-plan-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-encounter-btn">
                  {saving ? 'Saving...' : 'Save Encounter'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Encounters;
